import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * /lite/subprocessors — Public sub-processor list
 *
 * Required by GDPR Art. 28 for transparency to data subjects (and clinic
 * customers signing DPAs with us). Updated when we add or change vendors.
 */
const SUBPROCESSORS = [
  {
    name: 'OpenAI',
    purpose: 'LLM inference (GPT-4o, GPT-4o-mini)',
    data: 'Prompt text + response. For Lite: de-identified inputs only. For Full: clinical inputs (with user consent).',
    region: 'United States',
    appliesTo: ['Full', 'Lite'],
    dpa: 'https://openai.com/policies/data-processing-addendum',
  },
  {
    name: 'Anthropic',
    purpose: 'LLM inference (Claude Sonnet 4.5)',
    data: 'Prompt text + response (Full app reports). De-identified for Lite if used.',
    region: 'United States',
    appliesTo: ['Full'],
    dpa: 'https://www.anthropic.com/legal/dpa',
  },
  {
    name: 'Emergent.sh',
    purpose: 'Application hosting + LLM proxy',
    data: 'All HTTP traffic. Request bodies not logged.',
    region: 'United States (EU available on request)',
    appliesTo: ['Full', 'Lite'],
    dpa: 'Contact emergent for DPA copy',
  },
  {
    name: 'MongoDB Atlas',
    purpose: 'Primary database',
    data: 'All persistent data described in our Privacy Policy §2 and §3.',
    region: 'US-East by default; EU available on request',
    appliesTo: ['Full', 'Lite'],
    dpa: 'https://www.mongodb.com/legal/dpa',
  },
  {
    name: 'Cloudflare',
    purpose: 'CDN, DDoS protection, IP geolocation',
    data: 'HTTP request metadata, IP addresses (we receive hashed copies)',
    region: 'Global anycast',
    appliesTo: ['Full', 'Lite'],
    dpa: 'https://www.cloudflare.com/cloudflare-customer-dpa',
  },
  {
    name: 'Resend',
    purpose: 'Transactional email delivery',
    data: 'Recipient email + email body (e.g. when a user emails themselves a generated report)',
    region: 'United States; EU available',
    appliesTo: ['Full', 'Lite'],
    dpa: 'https://resend.com/legal/dpa',
  },
  {
    name: 'Paystack',
    purpose: 'Payment processing',
    data: 'Therapist email + Paystack-hosted payment details (card data never touches our servers)',
    region: 'Nigeria, South Africa',
    appliesTo: ['Full', 'Lite'],
    dpa: 'https://paystack.com/data-protection-agreement',
  },
  {
    name: 'PostHog',
    purpose: 'Product analytics + session recording (with input masking)',
    data: 'Aggregated event data + masked session recordings. Hard-disabled on PlayFirst Lite (/us, /lite).',
    region: 'United States',
    appliesTo: ['Full'],
    dpa: 'https://posthog.com/dpa',
  },
];

export default function Subprocessors() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="subprocessors-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/privacy" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Privacy Policy
        </Link>
        <h1 className="text-3xl font-serif text-primary-900 mb-2">Sub-processors</h1>
        <p className="text-sm text-stone-500 mb-8">Last updated: 28 February 2026</p>
        <p className="text-stone-700 mb-8 max-w-2xl">
          These are all the third parties we engage to run PlayFirst. Each has a Data Processing Agreement in place. We will notify Full account holders by email at least 30 days before adding or changing any sub-processor that handles personal data.
        </p>

        <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="subprocessors-table">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Vendor</th>
                <th className="text-left px-4 py-3 font-semibold">Purpose</th>
                <th className="text-left px-4 py-3 font-semibold">Data shared</th>
                <th className="text-left px-4 py-3 font-semibold">Region</th>
                <th className="text-left px-4 py-3 font-semibold">Applies to</th>
                <th className="text-left px-4 py-3 font-semibold">DPA</th>
              </tr>
            </thead>
            <tbody>
              {SUBPROCESSORS.map((sp) => (
                <tr key={sp.name} className="border-t border-stone-100 align-top">
                  <td className="px-4 py-3 font-medium text-primary-900">{sp.name}</td>
                  <td className="px-4 py-3 text-stone-700">{sp.purpose}</td>
                  <td className="px-4 py-3 text-stone-700 max-w-xs">{sp.data}</td>
                  <td className="px-4 py-3 text-stone-700">{sp.region}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {sp.appliesTo.map((p) => (
                        <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full ${p === 'Lite' ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-700'}`}>{p}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {sp.dpa.startsWith('http')
                      ? <a href={sp.dpa} target="_blank" rel="noreferrer" className="text-primary underline">View →</a>
                      : <span className="text-stone-500">{sp.dpa}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-stone-500 mt-6">
          Want a copy of a signed DPA for your clinic? Email <a href="mailto:michaela@playfirstapp.com" className="underline">michaela@playfirstapp.com</a>.
        </p>
      </div>
    </div>
  );
}
